import { ChangeEvent, FunctionComponent } from "react";

import { faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FieldError } from "react-hook-form";

import { Icon } from "@/components";
import { InputField } from "@/components/InputField/InputField";

import { Subsection } from "../schema/deep-research-prompt";

interface SubsectionError {
	header?: FieldError;
	instruction?: FieldError;
}

interface SubsectionEditorProps {
	subsections: Subsection[];
	onChange: (subsections: Subsection[]) => void;
	errors?: SubsectionError[];
}

export const SubsectionEditor: FunctionComponent<SubsectionEditorProps> = ({
	subsections,
	onChange,
	errors,
}) => {
	const handleAdd = () => {
		onChange([...subsections, { order: subsections.length + 1, header: "", instruction: "" }]);
	};

	const handleRemove = (index: number) => {
		const updated = subsections
			.filter((_, i) => i !== index)
			.map((s, i) => ({ ...s, order: i + 1 }));

		onChange(updated);
	};

	const handleChange = (index: number, field: keyof Subsection, value: string | number) => {
		const updated = subsections.map((s, i) => (i === index ? { ...s, [field]: value } : s));

		onChange(updated);
	};

	return (
		<div className="ml-4 border-l-2 border-gray-200 pl-4">
			<div className="flex items-center justify-between mb-2">
				<span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
					Subsections
				</span>
				<button
					className="flex items-center gap-1 text-xs text-blue-action hover:text-blue-action/80 cursor-pointer"
					type="button"
					onClick={handleAdd}
				>
					<Icon className="w-3 h-3" icon={faPlus} />
					Add Subsection
				</button>
			</div>
			{subsections.map((sub, index) => (
				<div
					key={index}
					className="relative bg-brand-white rounded-md p-3 mb-2 border border-gray-200"
				>
					<div className="flex items-center justify-between mb-1">
						<span className="text-xs font-medium text-text-meta">Subsection {sub.order}</span>
						<button
							className="text-red-400 hover:text-red-600 cursor-pointer"
							type="button"
							onClick={() => handleRemove(index)}
						>
							<Icon className="w-3 h-3" icon={faTrash} />
						</button>
					</div>
					<InputField
						error={!!errors?.[index]?.header}
						handleChange={(e: ChangeEvent<HTMLInputElement>) =>
							handleChange(index, "header", e.target.value)
						}
						label="Header"
						name={`subsection-header-${index}`}
						placeholder="e.g. Business Model & Customer Segments"
						value={sub.header}
					/>
					{errors?.[index]?.header && (
						<span className="text-xs text-red-500 -mt-1">{errors[index].header?.message}</span>
					)}
					<InputField
						multiline
						error={!!errors?.[index]?.instruction}
						handleChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
							handleChange(index, "instruction", e.target.value)
						}
						label="Instruction"
						name={`subsection-instruction-${index}`}
						placeholder="Describe what this subsection should cover..."
						rows={3}
						value={sub.instruction}
					/>
					{errors?.[index]?.instruction && (
						<span className="text-xs text-red-500 -mt-1">{errors[index].instruction?.message}</span>
					)}
				</div>
			))}
			{subsections.length === 0 && (
				<p className="text-xs text-gray-400 italic">No subsections added.</p>
			)}
		</div>
	);
};
